import { useEffect } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'

import {
  useAcceptInvitation,
  useInvitationPreview,
  useInvitationStatus,
  useMe,
} from '../../hooks/queries'

/**
 * Die Einladungsseite.
 *
 * Sie ist auch ohne Anmeldung erreichbar -- ein Einladungslink landet
 * regelmäßig bei jemandem, der noch kein Konto hat. Angezeigt wird nur, um
 * welche Reise es geht und von wem; aus dem Verlauf steht hier nichts.
 *
 * Seit dem bestätigten Beitritt hat sie drei Zustände: einladen, warten,
 * drin. Welcher gilt, weiß nur der Server -- die Seite fragt ihn.
 */
export function JoinPage() {
  const { code = '' } = useParams()
  const me = useMe()
  const preview = useInvitationPreview(code)
  const status = useInvitationStatus(code, me.data !== undefined)
  const accept = useAcceptInvitation()
  const navigate = useNavigate()

  // Wer schon Mitglied ist, soll nicht auf einer Einladungsseite hängen
  // bleiben -- das passiert, wenn man den eigenen Link ein zweites Mal öffnet.
  const drin = accept.data?.trip?.id ?? (status.data?.state === 'joined' ? status.data.trip_id : null)
  useEffect(() => {
    if (drin !== null) navigate(`/t/${drin}`, { replace: true })
  }, [drin, navigate])

  if (preview.isPending) {
    return (
      <div className="centered">
        <p className="empty__title">Einladung wird geprüft …</p>
      </div>
    )
  }

  if (preview.isError) {
    return (
      <div className="centered">
        <div className="card">
          <h1>Einladung ungültig</h1>
          <p className="card__lead">{preview.error.message}</p>
          <Link className="button" to="/">
            Weiter zur Anwendung
          </Link>
        </div>
      </div>
    )
  }

  // Angeklopft und noch keine Antwort. Was hier steht, steht auch dann da,
  // wenn der Reisende abgelehnt hat -- eine mitgeteilte Ablehnung wäre eine
  // Aufforderung nachzufragen.
  const wartet = accept.data?.state === 'pending' || status.data?.state === 'pending'

  // Wer angeklopft hat und sonst nirgends mitliest, gehört auf die Wartetafel
  // und nicht auf eine zweite Karte mit demselben Text. Dort steht dasselbe --
  // und dazu ein Weg hinaus.
  if (wartet && me.data !== undefined && !me.data.may_use_app) {
    return <Navigate to="/" replace />
  }

  if (wartet) {
    return (
      <div className="centered">
        <div className="card">
          <h1>{preview.data.trip_title}</h1>
          <p className="card__lead">
            Deine Anfrage ist bei {preview.data.owner_name}. Sobald sie
            bestätigt ist, steht der Reisebericht in deiner Liste.
          </p>
          <Link className="button button--outline" to="/">
            Weiter zur Anwendung
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="centered">
      <div className="card">
        <h1>{preview.data.trip_title}</h1>
        <p className="card__lead">
          {preview.data.owner_name} lädt dich ein, den Reisebericht mitzulesen.
        </p>

        <p className="notice notice--info">
          Du bekommst alle Beiträge zu lesen. Deine Antworten sieht
          ausschließlich {preview.data.owner_name} – die anderen Empfänger
          nicht.
        </p>

        {preview.data.approval_required && (
          <p className="card__lead">
            {preview.data.owner_name} bestätigt jeden Beitritt. Du liest mit,
            sobald das geschehen ist.
          </p>
        )}

        {accept.isError && <p className="notice" role="alert">{accept.error.message}</p>}

        {me.data ? (
          <div className="stack">
            <button
              className="button"
              type="button"
              disabled={accept.isPending}
              onClick={() => accept.mutate(code)}
            >
              {accept.isPending
                ? 'Einen Moment …'
                : preview.data.approval_required
                  ? 'Beitritt anfragen'
                  : 'Beitreten'}
            </button>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
              Angemeldet als {me.data.display_name}
            </p>
          </div>
        ) : (
          <div className="stack">
            <Link className="button" to={`/register?code=${preview.data.code}`}>
              Konto anlegen und beitreten
            </Link>
            <Link
              className="button button--outline"
              to="/login"
              state={{ from: `/join/${preview.data.code}` }}
            >
              Ich habe schon ein Konto
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}
